import React from 'react';
import styled from 'styled-components';

import TextComponent from '@/components/TextComponent';
import FormInputComponent from '@/components/FormInputComponent';
import ButtonComponent from '@/components/ButtonComponent';
import GlobalAlert from '@/components/GlobalAlert';
import { validateAccount, validateBusinessNumber, validateName, validatePharmacyName } from '@/utils/validate';
import SelectComponent from '@/components/SelectComponent';
import { SelectOption } from '@/assets/data/selectOptions';
import { AcceptedFiles } from '../SignupPage';
import theme from '@/styles/theme';

interface Props {
  display: boolean;
  control: any;
  errors: any;
  addImageFile: (file: Array<File>, type: string) => void;
  fileName: any;
  banks: Array<SelectOption>;
  selectBank: any;
}

const PharmacyInfo = (props: Props) => {
  const {
    control,
    errors,
    addImageFile,
    fileName,
    banks,
    selectBank,
    display
  } = props;

  const fileAccept: AcceptedFiles = [
    'application/pdf',
    'image/jpg',
    'image/jpeg',
    'image/png'
  ];

  const bankbookFileInput = React.useRef<HTMLInputElement>(null);
  const corporateRegistrationFileInput = React.useRef<HTMLInputElement>(null);

  const handleChange = (event: any, type: string) => {
    const fileUploaded: Array<File> = Array.from(event.target.files);

    if (fileUploaded[0].size > 10000000)
      return GlobalAlert.show({
        title: '파일 사이즈가 10MB 이하여야 합니다.'
      });

    fileUploaded.map((file: any) => {
      return Object.assign(file, {
        blob: URL.createObjectURL(file)
      });
    });
    addImageFile(fileUploaded, type);
  };

  return (
    <InfoSection display={display}>
      <Top>
        <OrangeBar />
        <InfoSectionTitle>사업자 등록 정보</InfoSectionTitle>
      </Top>
      <Body>
        <FormInputComponent
          keyName="accountName"
          label={'정산은행'}
          placeholder={'예금주명'}
          defaultValue={''}
          control={control}
          error={errors.accountName}
          rules={{
            required: '정산은행 정보를 입력해주세요',
            validate: validateName
          }}
          maxLength={15}
          leftElement={
            <SelectComponent
              options={banks}
              defaultValue={banks && banks[0]}
              placeholder={'은행선택'}
              disabled={false}
              onChange={e => selectBank(e)}
              selectStyle={{
                width: '200px',
                height: '61.7px'
              }}
            />
          }
        />
        <FormInputComponent
          keyName="account"
          label={'계좌번호'}
          placeholder={'계좌번호를 입력해주세요'}
          defaultValue={''}
          control={control}
          error={errors.account}
          rules={{
            required: '정산받으실 계좌번호를 입력주세요',
            validate: validateAccount
          }}
          maxLength={20}
        />
        <FormInputComponent
          keyName="bankbookFileName"
          value={fileName.bankbook || '파일을 선택해주세요'}
          disabled
          label={'통장사본'}
          rightElement={
            <ButtonComponent
              bgColor={theme.DF_COLOR.GREEN500}
              borderColor={theme.DF_COLOR.GREEN500}
              textColor={theme.DF_COLOR.WHITE}
              fontWeight={600}
              fontSize={20}
              text={'파일선택'}
              onClick={() => bankbookFileInput?.current?.click()}
            />
          }
          control={control}
          rules={{
            minLength: { value: 1, message: '통장사본을 확인해주세요' }
          }}
        />
        <input
          type="file"
          ref={bankbookFileInput}
          onChange={e => handleChange(e, 'bankbook')}
          style={{ display: 'none' }}
          accept={fileAccept.toString()}
        />
        <FormInputComponent
          keyName="brandName"
          label={'상호명'}
          placeholder={'상호명을 입력해주세요'}
          defaultValue={''}
          control={control}
          error={errors.brandName}
          rules={{
            required: '상호명을 입력해주세요',
            validate: validatePharmacyName
          }}
          maxLength={15}
        />
        <FormInputComponent
          keyName="businessLicenseNumber"
          label={'사업자등록번호'}
          placeholder={'사업자등록번호을 입력해주세요'}
          defaultValue={''}
          control={control}
          error={errors.businessLicenseNumber}
          rules={{
            required: '사업자등록번호를 입력해주세요',
            validate: validateBusinessNumber
          }}
          maxLength={15}
        />
        <FormInputComponent
          keyName="representativeName"
          label={'대표자명'}
          placeholder={'대표자명을 입력해주세요'}
          defaultValue={''}
          control={control}
          error={errors.representativeName}
          rules={{
            required: '대표자명을 입력해주세요',
            validate: validateName
          }}
          maxLength={15}
        />
        <FormInputComponent
          keyName="corporateRegistrationFileName"
          value={fileName.corporateRegistration || '파일을 선택해주세요'}
          disabled
          label={'사업자등록증'}
          rightElement={
            <ButtonComponent
              bgColor={theme.DF_COLOR.GREEN500}
              borderColor={theme.DF_COLOR.GREEN500}
              textColor={theme.DF_COLOR.WHITE}
              fontWeight={600}
              fontSize={20}
              text={'파일선택'}
              onClick={() => corporateRegistrationFileInput?.current?.click()}
            />
          }
          control={control}
          rules={{
            minLength: { value: 1, message: '사업자등록증을 확인해주세요' }
          }}
        />
        <input
          type="file"
          ref={corporateRegistrationFileInput}
          onChange={e => handleChange(e, 'corporateRegistration')}
          style={{ display: 'none' }}
          accept={fileAccept.toString()}
        />
      </Body>
    </InfoSection>
  );
};

export default PharmacyInfo;

const InfoSection = styled.div<{ display: boolean }>`
  display: ${({ display }) => (display ? 'block' : 'none')};
`;
const Top = styled.div`
  display: flex;
  margin-bottom: 45px;
`;
const OrangeBar = styled.div`
  background: #ff8d00;
  width: 4px;
  margin-right: 8px;
`;
const InfoSectionTitle = styled(TextComponent)`
  font-size: 20px;
  font-weight: 700;
`;
const Body = styled.div`
  padding-left: 10px;
`;
